import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useMemo } from "react";
import { Page, Panel } from "~/components/ui/page";
import { chapterSource, guideSections } from "~/features/help/guide";
import { Markdown } from "~/features/help/markdown";
import { HELP_CHAPTERS, type HelpChapter } from "~/features/help/registry";
import { useLocale, useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/help/$chapter")({
  beforeLoad: ({ params }) => {
    if (!(HELP_CHAPTERS as readonly string[]).includes(params.chapter)) throw notFound();
  },
  component: HelpChapterPage,
});

function HelpChapterPage() {
  const t = useT();
  const [locale] = useLocale();
  const chapter = Route.useParams().chapter as HelpChapter;
  const source = chapterSource(locale, chapter);
  const toc = useMemo(() => guideSections(locale).filter((s) => s.chapter === chapter), [locale, chapter]);
  return (
    <Page className="max-w-6xl">
      <Link to="/help" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
        <ChevronLeft className="size-4" aria-hidden />
        {t("help.ui.title")}
      </Link>
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-8">
        <Panel className="px-5 py-6 md:px-8">
          <Markdown source={source} />
        </Panel>
        <nav aria-label={t("help.ui.contents")} className="hidden lg:block">
          <div className="sticky top-4">
            <p className="mb-2 px-2 text-[11px] font-medium tracking-wide text-fg-3 uppercase">{t("help.ui.contents")}</p>
            <ul className="flex flex-col">
              {toc.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="block rounded-control px-2 py-1 text-xs text-fg-2 hover:bg-sunken hover:text-fg"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      </div>
    </Page>
  );
}
